import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import './OrderHistoryGrid.css';
import SupplyShippedIcon from "../../../assets/images/supplies-shipped-icon.svg";
import InvoicePaidIcon from "../../../assets/images/invoice-paid.svg";
import ProductIcon from "../../../assets/images/product-icon.svg";
import ColorDropIcon from "../../../assets/images/color-drop-icon.svg";
import RightArrowWhiteIcon from "../../../assets/images/rightArrowWhiteIcon.svg";
import { useState } from "react";
import { DisplayDate } from "../../../utils/DisplayDate";
import { formatNumberOnLocale, formatAddrs } from "../../../constants/AppMethod";
import { Button } from 'reactstrap';
import { useTranslation } from "react-i18next";
import HeaderComponent from "../../shared/header/HeaderComponent/HeaderComponent";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { DownloadOrderInvoice } from "../../../actions/Dashboard";
import { getUserSessionData } from "../../../utils/Authentication/handleUserSession";
import Loader from "../../shared/loader/Loader";
import { ORDER_TRANSACTION_TYPES } from "../../../constants/AppConstants";
import { useQuery } from "../../../utils/CustomHooks/UseQuery";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

const OrderHistoryGrid = (props) => {
  let detailId = useRef(''); 
  let deviceName = useRef('');
  let transType = useRef('');

  const searchParams = useQuery();

  const tableRef = useRef(null);
  const { orderHistory } = props;
  const supplyShippedHistory = orderHistory?.supplyShippedHistory || [];
  const invoiceRaisedHistory = orderHistory?.invoiceRaisedHistory || [];
  const { t } = useTranslation(["translation"]);
  const DownloadOrderInvoiceResult = useSelector((state)=>state.DownloadOrderInvoice);
  const dispatch = useDispatch();

      // Collect unique device names from both histories
  const deviceNamesSet = new Set();
  invoiceRaisedHistory.forEach(inv => inv.deviceName && deviceNamesSet.add(inv.deviceName));
  supplyShippedHistory.forEach(ord => ord.deviceName && deviceNamesSet.add(ord.deviceName));
  const deviceNames = Array.from(deviceNamesSet);

  // Device filter state
  const [selectedDevice, setSelectedDevice] = useState(t("ORDERHISTORY.FILTER.ALL_DEVICES"));

   // Transaction type filter
  const [transactionType, setTransactionType] = useState(ORDER_TRANSACTION_TYPES?.ALL);

    // Filter invoice and supply histories based on selected device
  const filteredInvoiceHistory = selectedDevice === t("ORDERHISTORY.FILTER.ALL_DEVICES")
    ? invoiceRaisedHistory
    : invoiceRaisedHistory.filter(inv => inv.deviceName === selectedDevice);

  const filteredSupplyHistory = selectedDevice === t("ORDERHISTORY.FILTER.ALL_DEVICES")
    ? supplyShippedHistory
    : supplyShippedHistory.filter(ord => ord.deviceName === selectedDevice);

   // Combine and sort all orders by date (descending)
  let allOrders = [];
  if (transactionType === ORDER_TRANSACTION_TYPES?.ALL) {
    allOrders = [
      ...filteredInvoiceHistory.map(inv => ({ ...inv, type: ORDER_TRANSACTION_TYPES?.INVOICE })),
      ...filteredSupplyHistory.map((ord, idx) => ({ ...ord, type: ORDER_TRANSACTION_TYPES?.SUPPLY, supplyIndex: idx }))
    ];
  } else if (transactionType === ORDER_TRANSACTION_TYPES?.INVOICE) {
    allOrders = filteredInvoiceHistory.map(inv => ({ ...inv, type: ORDER_TRANSACTION_TYPES?.INVOICE }));
  } else if (transactionType === ORDER_TRANSACTION_TYPES?.SUPPLY) {
    allOrders = filteredSupplyHistory.map((ord, idx) => ({ ...ord, type: ORDER_TRANSACTION_TYPES?.SUPPLY, supplyIndex: idx }));
  }
  allOrders.sort((a, b) => new Date(b.displayDateFormat || b.date) - new Date(a.displayDateFormat || a.date));
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [expandedSupplyIndex, setExpandedSupplyIndex] = useState(null);

  const totalPages = Math.ceil(allOrders.length / pageSize);

  const paginatedOrders = allOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const TRANSACTION_TYPES = [
  { value: ORDER_TRANSACTION_TYPES?.ALL, label: t("ORDERHISTORY.TRANSACTIONS.ALL")},
  { value: ORDER_TRANSACTION_TYPES?.INVOICE, label: t("ORDERHISTORY.TRANSACTIONS.INVOICE") },
  { value: ORDER_TRANSACTION_TYPES?.SUPPLY, label: t("ORDERHISTORY.TRANSACTIONS.SUPPLIES_SHIPPED") }
];

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    setExpandedSupplyIndex(null);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
    setExpandedSupplyIndex(null);
  };

  const handleTransactionTypeChange = (e) => {
    if( detailId.current ){
      detailId.current = '';
      deviceName.current = '';
      transType.current = '';
    }
    setTransactionType(e.target.value);
    setCurrentPage(1);
    setExpandedSupplyIndex(null);
  };

  const handleDeviceChange = (e) => {
    if( detailId.current ){
      detailId.current = '';
      deviceName.current = '';
      transType.current = '';
    }
    setSelectedDevice(e.target.value);
    setCurrentPage(1);
    setExpandedSupplyIndex(null);
  };

   // Pagination numbers logic: show max 10 page numbers at a time
  const getPageNumbers = () => {
    const maxPagesToShow = 10;
    let start = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let end = start + maxPagesToShow - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxPagesToShow + 1);
    }
    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getSupplyShippedDetails = (order) => {
    const inkColorShipped = order.supplyShippedProducts.map(product => product.productColor )
    return `${t("ORDERHISTORY.TRANSACTIONS.SUPPLIES_SHIPPED")} : ${inkColorShipped.join(', ')}`;
  }

  const onDownloadOrderInvoice = (invoiceId, date) => {
    const UID = getUserSessionData('UserId');
    dispatch(DownloadOrderInvoice(UID, invoiceId, date));
  }

  useEffect( () =>{
    
    if( searchParams?.size ){
      detailId.current = searchParams.get('detailId');
      deviceName.current = searchParams.get('deviceName');
      transType.current = searchParams.get('transType');

      setSelectedDevice( deviceName.current );
      setTransactionType( transType.current );
    }

    const element = tableRef.current;
    if(element !== null)
    {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    // eslint-disable-next-line
  },[] )

  if (DownloadOrderInvoiceResult.loading) {
    return <Loader></Loader>
  }

  return (
    <div className="table-scroll-wrapper">
      <HeaderComponent title={t("ORDERHISTORY.TITLE")} description={t("ORDERHISTORY.DESCRIPTION")} />
      <div className='filter-portions'>
        <div> {t("ORDERHISTORY.FILTER.SHOWING")} </div>
        <div>
          <select className='select-box' name="deviceTypes" id="deviceTypes"  value={selectedDevice}
            onChange={handleDeviceChange}>
            <option id="allDevices"> {t("ORDERHISTORY.FILTER.ALL_DEVICES")} </option>
            {deviceNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <select className='select-box' value={transactionType} onChange={handleTransactionTypeChange}>
            {TRANSACTION_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <label>
            {t("ORDERHISTORY.PAGE_OPTION.ROWS_PER_PAGE")}:&nbsp;
            <select value={pageSize} onChange={handlePageSizeChange}>
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <table className="order-history-table">
        <thead>
          <tr>
            <th></th>
            <th scope="col"> {t("ORDERHISTORY.TABLE.DATE")} </th>
            <th scope="col"> {t("ORDERHISTORY.TABLE.ITEM")} </th>
            <th scope="col"> {t("ORDERHISTORY.TABLE.DEVICE")} </th>
          </tr>
        </thead>
        <tbody>
          {paginatedOrders.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: "center" }}>
                {t("ORDERHISTORY.TABLE.NO_RECORDS_FOUND")}
              </td>
            </tr>
          )}
          {paginatedOrders.map((order, idx) => {
            if (order.type === ORDER_TRANSACTION_TYPES?.INVOICE) {
              const highlightDetailElem = detailId.current === order.invoiceId;
              return (
                <tr key={`invoice-${order.invoiceId || idx}`} id={order.invoiceId} ref={tableRef} className={`${highlightDetailElem ? 'highlightDetail' : "" }`}>
                  <td><img src={InvoicePaidIcon} alt='InvoicePaidIcon' /></td>
                  <td>{order.displayDateFormat}</td>
                  <td>
                    <div>
                      <span>
                        {t("DASHBOARD.ORDERS.SUBSCRIPTION_MONTH", {
                          subscription_month: DisplayDate(order.date, { month: "long" }),
                        })}
                      </span>
                      <span> | </span>
                      <span>{formatNumberOnLocale(order?.charge, order.currency)}</span>
                    </div>
                    <div className='item-styles'>
                      <span className='order-link' onClick={() => 
                          {
                            if( detailId.current ){
                              detailId.current = '';
                              deviceName.current = '';
                              transType.current = '';
                            }
                            onDownloadOrderInvoice(order?.invoiceId, order?.date)
                          }
                          }>
                        {t("ORDERHISTORY.TABLE.INVOICE_TYPE.INVOICE_DETAIL")}
                      </span>
                    </div>
                  </td>
                  <td >{order?.deviceName}</td>
                </tr>
              );
            } else if (order.type === ORDER_TRANSACTION_TYPES?.SUPPLY) {
              const highlightDetailElem = detailId.current === order.orderId;
              let isExpanded = ( highlightDetailElem ) ? true : expandedSupplyIndex === order.supplyIndex;
              return (
                <React.Fragment key={`supply-${order.orderId || idx}`}>
                  <tr className={`${isExpanded ? 'remove-border' : ''} ${highlightDetailElem ? 'highlightDetail' : "" }`} id={order.orderId} ref={tableRef}>
                    <td><img src={SupplyShippedIcon} alt='SupplyShippedIcon' /></td>
                    <td>{order.displayDateFormat}</td>
                    <td>
                      <div>
                        <span>{order.orderType === 'SupplyShipped' ? getSupplyShippedDetails(order) : order.orderType}</span>
                        <span> {order?.amount}</span>
                      </div>
                      <div className='item-styles'>
                        <span
                          className='order-link'
                          onClick={e => {
                            if( detailId.current ){
                              detailId.current = '';
                              deviceName.current = '';
                              transType.current = '';
                            }
                            e.preventDefault();
                            setExpandedSupplyIndex(isExpanded ? null : order.supplyIndex);
                          }}
                        >
                          {t("ORDERHISTORY.TABLE.SUPPLY_TYPE.TRACK_ORDER_NO")} {order.orderId}
                        </span>
                      </div>
                    </td>
                    <td >{order?.deviceName}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td></td>
                      <td></td>
                      <td>
                        {t("ORDERHISTORY.TABLE.EXPANDED_DETAILS.SHIPPING_ADDRESS")} {formatAddrs(order?.shippingAddress)}
                        <table className='ordered-items-table'>
                          <thead>
                            <tr>
                              <th>
                                <span className='display-flex'>
                                  <img src={ProductIcon} alt='Product-icon' /> {t("ORDERHISTORY.TABLE.EXPANDED_DETAILS.PRODUCT")}
                                </span>
                              </th>
                              <th>
                                <span className='display-flex'>
                                  <img src={ColorDropIcon} alt='Color-Drop-icon' /> {t("ORDERHISTORY.TABLE.EXPANDED_DETAILS.COLOR")}
                                </span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {order?.supplyShippedProducts?.map((ShippedProd, i) => (
                              <tr key={i}>
                                <td>{ShippedProd?.productCode}</td>
                                <td>{ShippedProd?.productColor}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan={3} className='track-order-btn'>
                                <CopyToClipboard text={order.orderId} onCopy={() => {}}>
                                  <Button>
                                    {t("ORDERHISTORY.TABLE.EXPANDED_DETAILS.TRACK_ORDER")} <img src={RightArrowWhiteIcon} alt='RightArrowWhiteIcon' />
                                  </Button>
                                </CopyToClipboard>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            }
            return null;
          })}
        </tbody>
      </table>
      <div className='pagination'>
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          &lt; {t("ORDERHISTORY.TABLE.PAGINATION.PREV")}
        </button>
         {getPageNumbers().map(pageNum => (
          <button
            key={pageNum}
            onClick={() => handlePageChange(pageNum)}
            className={pageNum === currentPage ? 'active-page' : ''}
          >
            {pageNum}
          </button>
        ))}

        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
           &gt; {t("ORDERHISTORY.TABLE.PAGINATION.NEXT")}
        </button>
      </div>
    </div>
  );
};

export default OrderHistoryGrid;